import React, { useState, useEffect, useSyncExternalStore } from 'react';
import { subscribeUser, getUserVersion } from '../data/userStore';
import { getPatternPresets, savePatternPreset, deletePatternPreset } from '../utils/presetManager';
import {
  getNotesInMode,
  calculateStartFret,
  getNoteAtFret,
  getGuitarTuning,
  getNotesPerString
} from './GuitarNeckVisualizer.utils';
import { getScaleForActiveChord } from '../utils/guitarNeckUtils';
import { MODES, ALL_NOTES, resolveAllNotesIndex, scaleIndexSet, getVoicingTonePcs, getChordDegreePc } from '../utils/musicTheory';

// Accordage actif, reactif : le miroir user est hydrate apres le premier rendu,
// donc un composant qui lit getGuitarTuning() sans s'abonner garde EADGBE dans
// ses memos jusqu'a un autre changement d'etat.
export const useGuitarTuning = () => {
  useSyncExternalStore(subscribeUser, getUserVersion, getUserVersion);
  return getGuitarTuning();
};

// Hook for managing scale and mode state
export const useScaleLogic = (rootNote, currentMode, activeChordNote, chordExtension = '3', trackedDegree = 1) => {
  // useMemo (not effect + setState): computing in render avoids the second
  // full-neck render per chord change that the effect version caused.
  const activeScale = React.useMemo(() => {
    const scale = getScaleForActiveChord(rootNote, currentMode, activeChordNote);
    // Pentatonic/blues: surface the parent scale's missing notes as ghosts so the
    // neck explains where the borrowed chords come from.
    // voicingTonePcs: pitch classes of the sounding chord. Notes in this set read
    // as strong (target notes), everything else dims to "passage". Follows the
    // drone/triad/7th toggle, so the 7th promotes from passage to target live.
    // anchorPc: pitch class the neck paints green. It follows the tracked chord
    // degree so a voice-leading drill can hold onto the 3rd or the 7th across the
    // progression instead of the root, which barely moves. tonicPc stays around
    // so the old anchor can keep a thin outline and the tonal bearing survives.
    // Degree 1 resolves to the chord root, i.e. exactly the pre-existing green.
    const tonicPc = resolveAllNotesIndex(scale.scaleRoot);
    const degreePc = getChordDegreePc(rootNote, currentMode, scale.scaleRoot, trackedDegree);
    return {
      ...scale,
      // Ghost notes (parent-scale notes missing from the pentatonic) are hidden
      // for now; re-enable with getGhostNotes(rootNote, currentMode) when the
      // feature comes back.
      ghostNotes: [],
      tonicPc,
      anchorPc: degreePc === -1 ? tonicPc : degreePc,
      voicingTonePcs: getVoicingTonePcs(rootNote, currentMode, activeChordNote, chordExtension)
    };
  }, [rootNote, currentMode, activeChordNote, chordExtension, trackedDegree]);

  return { activeScale };
};

// Hook for managing string visibility
export const useStringVisibility = () => {
  const [visibleStrings, setVisibleStrings] = useState(null);

  // Stable identity (functional update) so memoized StringRow props don't churn
  const toggleStringVisibility = React.useCallback((stringIndex) => {
    setVisibleStrings(prev => {
      if (prev === null) {
        // First click - isolate just this string
        return new Set([stringIndex]);
      } else if (prev.has(stringIndex) && prev.size === 1) {
        // Clicking the only visible string - reset to show all strings
        return null;
      } else if (prev.has(stringIndex)) {
        // Clicking an already visible string - remove it from visible set
        const newVisibleStrings = new Set(prev);
        newVisibleStrings.delete(stringIndex);

        // If we removed the last string, reset to show all
        return newVisibleStrings.size === 0 ? null : newVisibleStrings;
      } else {
        // Clicking a new string - add it to visible set
        const newVisibleStrings = new Set(prev);
        newVisibleStrings.add(stringIndex);
        return newVisibleStrings;
      }
    });
  }, []);

  return { visibleStrings, toggleStringVisibility, setVisibleStrings };
};

// Hook for managing note selection
export const useNoteSelection = () => {
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [hasSelectedNote, setHasSelectedNote] = useState(false);
  
  const handleNoteClick = (stringIndex, fret, note) => {
    
    // If clicking the already selected note, deselect it
    if (selectedNotes.find(n => n.stringIndex === stringIndex && n.fret === fret)) {
      const newSelectedNotes = selectedNotes.filter(n => !(n.stringIndex === stringIndex && n.fret === fret));
      setSelectedNotes(newSelectedNotes);
      
      // Update hasSelectedNote based on whether there are any notes left
      setHasSelectedNote(newSelectedNotes.length > 0);
    } else {
      // Otherwise, select the new note (adding it to the existing selection)
      setSelectedNotes([...selectedNotes, {
        stringIndex,
        fret,
        note,
        // Add a timestamp to ensure state change is detected
        timestamp: Date.now()
      }]);
      
      // We now have at least one selected note
      setHasSelectedNote(true);
    }
  };
  
  return { 
    selectedNotes, 
    setSelectedNotes, 
    hasSelectedNote, 
    setHasSelectedNote, 
    handleNoteClick 
  };
};

// Generate the 3-notes-per-string pattern for a given scale.
// This is extracted so other consumers (like long-press paint mode) can reuse the logic.
export const generateNotePattern = (activeScale, options = {}) => {
  
  if (!activeScale || !activeScale.notes || activeScale.notes.length === 0) {
    return { pattern: [], rootFret: 0, lowestNoteOnAnyString: 0 };
  }
  
  const {
    anchorToStartNote = false,
    startStringIndex: rawStartStringIndex = 0,
    startFret: rawStartFret = 0
  } = options || {};
  const totalStrings = getGuitarTuning().length;
  const startStringIndex = Math.min(Math.max(Math.trunc(rawStartStringIndex), 0), totalStrings - 1);
  const startFret = Math.max(0, Math.trunc(rawStartFret));

  if (anchorToStartNote) {
    return generateAnchoredPattern(activeScale, {
      startStringIndex,
      startFret,
      totalStrings
    });
  }

  const pattern = [];
  
  // Get scale degrees for each note
  const getScaleDegree = (note) => activeScale.notes.indexOf(note);
  
  // Step 1: Find the first occurrence of the root note on the low E string between frets 1 and 14
  const lowEOpenNote = getGuitarTuning()[0];
  let rootFretOnLowE = -1;
  
  const rootIdx = resolveAllNotesIndex(activeScale.scaleRoot);
  
  // Check if the scale contains the open string note
  // If it does and the root matches, start from fret 12 to avoid open strings
  const scaleContainsOpenNote = activeScale.notes.includes(lowEOpenNote);
  
  if (scaleContainsOpenNote && activeScale.scaleRoot === lowEOpenNote) {
    // If the root is the same as the open string note, use fret 12 (octave)
    rootFretOnLowE = 12;
  } else {
    // For other roots, find the first occurrence on the low E string (avoiding open strings)
    for (let fret = 1; fret <= 14; fret++) {
      const fretIdx = (ALL_NOTES.indexOf(lowEOpenNote) + fret) % 12;
      if (fretIdx === rootIdx) {
        rootFretOnLowE = fret;
        break;
      }
    }
  }
  
  // If root not found between 1-14, use the default start fret
  if (rootFretOnLowE === -1) {
    rootFretOnLowE = calculateStartFret(activeScale);
  }
  
  // Determine scale type based on properties
  const modeData = MODES[activeScale.modeName];
  const scaleNoteCount = activeScale.notes ? activeScale.notes.length : 0;
  
  // Scales with <= 6 notes (pentatonic, blues) use special pattern generation
  // Scales with 7 notes (diatonic) use standard 3-notes-per-string
  // Scales with 8+ notes (bebop, diminished) use special non-diatonic logic
  const usePentatonicPattern = modeData && (modeData.isPentatonic || scaleNoteCount <= 6);
  const useNonDiatonicPattern = modeData && !modeData.isPentatonic && scaleNoteCount >= 8;
  
  // Step 2: Generate the scale pattern for each string
  // First, create an array of all scale notes with their positions across the neck
  const scaleMap = {};
  const scaleIdx = scaleIndexSet(activeScale.notes);
  
  // For each string, map out all notes that are in the scale
  getGuitarTuning().forEach((openNote, stringIndex) => {
    scaleMap[stringIndex] = [];
    
    // Check a wide range of frets to ensure we have enough notes
    for (let fret = 0; fret <= 24; fret++) {
      const fretIdx = (ALL_NOTES.indexOf(openNote) + fret) % 12;
      if (scaleIdx.has(fretIdx)) {
        const note = getNoteAtFret(openNote, fret);
        // Find the matching note in the scale (handling enharmonics)
        const scaleNote = activeScale.notes.find(n => resolveAllNotesIndex(n) === fretIdx);
        scaleMap[stringIndex].push({
          fret,
          note: scaleNote || note, // Use the scale's version of the note if found
          degree: getScaleDegree(scaleNote || note)
        });
      }
    }
    
    // Sort by fret position
    scaleMap[stringIndex].sort((a, b) => a.fret - b.fret);
  });
  
  // Step 3: Generate the pattern starting from the root note on the low E string
  // For the first string (E), find the root note and the next 2 notes in the scale
  const rootIndexOnLowE = scaleMap[0].findIndex(n => n.fret === rootFretOnLowE);
  
  // For pentatonic and blues scales (<=6 notes), use the special pattern generation
  if (usePentatonicPattern && activeScale.notes && activeScale.notes.length > 0) {
    // Build pentatonic pattern with sophisticated logic
    const MAX_SPAN = 5; // Maximum fret span per string
    
    let lastFretPrevString = null;
    let currentScaleDegree = 0; // Track position in the scale
    let threeCountStreak = 0;
    let twoCountStreak = 0;
    
    // Process each string from low to high (E to e)
    for (let stringIndex = 0; stringIndex < getGuitarTuning().length; stringIndex++) {
      const stringNotes = scaleMap[stringIndex];
      const frets = [];
      let remainingStringSpace = MAX_SPAN; // Max 5 frets between first and last notes on same string
      
      // 1) Find first note on this string
      let f1;
      
      if (stringIndex === 0) {
        // Low E string: start at root position
        f1 = rootFretOnLowE;
        // Find the correct scale degree index for the starting note
        currentScaleDegree = activeScale.notes.indexOf(activeScale.scaleRoot);
        if (currentScaleDegree === -1) currentScaleDegree = 0;
      } else {
        // Other strings: find the next scale degree closest to rootFretOnLowE position
        const nextScaleDegree = (currentScaleDegree + 1) % activeScale.notes.length;
        const targetNote = activeScale.notes[nextScaleDegree];
        const targetNoteIdx = resolveAllNotesIndex(targetNote);
        
        // Find this note on current string, preferring position closest to rootFretOnLowE
        f1 = null;
        let minDistance = Infinity;
        for (const noteInfo of stringNotes) {
          if (resolveAllNotesIndex(noteInfo.note) === targetNoteIdx) {
            const distance = Math.abs(noteInfo.fret - rootFretOnLowE);
            if (distance < minDistance) {
              minDistance = distance;
              f1 = noteInfo.fret;
            }
          }
        }
        
        if (f1 !== null) {
          currentScaleDegree = nextScaleDegree;
        }
      }
      
      if (f1 !== null && f1 !== undefined) {
        frets.push(f1);
        
        // 2) Try to add second note (f2)
        const secondDegree = (currentScaleDegree + 1) % activeScale.notes.length;
        const secondNote = activeScale.notes[secondDegree];
        const secondNoteIdx = resolveAllNotesIndex(secondNote);
        
        let f2 = null;
        for (const noteInfo of stringNotes) {
          if (resolveAllNotesIndex(noteInfo.note) === secondNoteIdx && 
              noteInfo.fret > f1 && 
              (noteInfo.fret - f1) <= MAX_SPAN) {
            f2 = noteInfo.fret;
            break;
          }
        }
        
        if (f2 !== null) {
          // Check if we have remaining space after adding f2
          const distanceF1toF2 = f2 - f1;
          remainingStringSpace -= distanceF1toF2;
          
          if (remainingStringSpace >= 1) {
            // We have space, add f2
            frets.push(f2);
            currentScaleDegree = secondDegree;
            
            // Check if we already used our "one large interval" allowance
            const hasLargeInterval = distanceF1toF2 > 1;
            
            // 3) Try to add third note (f3) - never add a 4th note
            const thirdDegree = (currentScaleDegree + 1) % activeScale.notes.length;
            const thirdNote = activeScale.notes[thirdDegree];
            const thirdNoteIdx = resolveAllNotesIndex(thirdNote);
            
            let f3 = null;
            for (const noteInfo of stringNotes) {
              if (resolveAllNotesIndex(noteInfo.note) === thirdNoteIdx && 
                  noteInfo.fret > f2 && 
                  (noteInfo.fret - f1) <= MAX_SPAN) {
                f3 = noteInfo.fret;
                break;
              }
            }
            
            if (f3 !== null) {
              const distanceF2toF3 = f3 - f2;
              
              // Only allow f3 if:
              // - We haven't used our large interval yet, OR
              // - This interval is small (1 fret)
              if (!hasLargeInterval || distanceF2toF3 <= 1) {
                remainingStringSpace -= distanceF2toF3;
                
                if (remainingStringSpace >= 1) {
                  // We have space, add f3
                  frets.push(f3);
                  currentScaleDegree = thirdDegree;
                }
              }
            }
          }
        }
        
        // Update streak counters
        if (frets.length === 3) {
          threeCountStreak++;
          twoCountStreak = 0;
        } else if (frets.length === 2) {
          twoCountStreak++;
          threeCountStreak = 0;
        }
        
        // Store pattern for this string
        pattern[stringIndex] = frets;
        
        // Update for next string
        if (frets.length > 0) {
          lastFretPrevString = frets[frets.length - 1];
        }
      } else {
        // No valid f1 found for this string
        pattern[stringIndex] = [];
      }
    }
    
    // Ensure each string has at least one note
    for (let stringIndex = 0; stringIndex < getGuitarTuning().length; stringIndex++) {
      if (!pattern[stringIndex] || pattern[stringIndex].length === 0) {
        if (scaleMap[stringIndex] && scaleMap[stringIndex].length > 0) {
          pattern[stringIndex] = [scaleMap[stringIndex][0].fret];
        } else {
          pattern[stringIndex] = [];
        }
      }
    }
    
    // Calculate lowest note for pentatonic pattern
    let lowestNote = Infinity;
    for (let stringIndex = 0; stringIndex < pattern.length; stringIndex++) {
      if (pattern[stringIndex] && pattern[stringIndex].length > 0) {
        const minFret = Math.min(...pattern[stringIndex]);
        if (minFret < lowestNote) {
          lowestNote = minFret;
        }
      }
    }
    lowestNote = lowestNote === Infinity ? 0 : lowestNote;
    
    return { pattern, rootFret: rootFretOnLowE, lowestNoteOnAnyString: lowestNote };
  }
  
  // For non-diatonic scales (8+ notes), use special pattern generation logic
  if (useNonDiatonicPattern && activeScale.notes && activeScale.notes.length > 0) {
    // Generate pattern for non-diatonic scales using half-step logic
    let currentFretPosition = rootFretOnLowE;
    
    // Find the root note in our scale map
    const rootNoteOnLowE = scaleMap[0].find(n => n.fret === rootFretOnLowE);
    if (!rootNoteOnLowE) {
      // Fallback to default pattern if root not found
      const notesPerString = getNotesPerString(activeScale.modeName);
      for (let stringIndex = 0; stringIndex < getGuitarTuning().length; stringIndex++) {
        pattern[stringIndex] = scaleMap[stringIndex].slice(0, notesPerString).map(n => n.fret);
      }
      return { pattern, rootFret: rootFretOnLowE, lowestNoteOnAnyString: 0 };
    }
    
    // Start with the root note on the lowest string
    pattern[0] = [rootFretOnLowE];
    let currentScaleIndex = activeScale.notes.indexOf(rootNoteOnLowE.note);
    
    // Process notes starting from the root
    // Add notes to the first string following half-step rule
    while (currentScaleIndex < activeScale.notes.length - 1) {
      const currentNote = activeScale.notes[currentScaleIndex];
      const nextNote = activeScale.notes[currentScaleIndex + 1];
      
      // Calculate interval to next note
      const currentNoteIdx = resolveAllNotesIndex(currentNote);
      const nextNoteIdx = resolveAllNotesIndex(nextNote);
      const interval = (nextNoteIdx - currentNoteIdx + 12) % 12;
      
      if (interval === 1) {
        // Half step - add to current string
        const nextNoteFret = currentFretPosition + interval;
        if (nextNoteFret <= 24) { // Stay within reasonable fret range
          pattern[0].push(nextNoteFret);
          currentFretPosition = nextNoteFret;
          currentScaleIndex++;
        } else {
          break; // Move to next string if we're too high on the fretboard
        }
      } else {
        // More than half step - move to next string
        break;
      }
    }
    
    // Continue pattern on remaining strings
    for (let stringIndex = 1; stringIndex < getGuitarTuning().length && currentScaleIndex < activeScale.notes.length - 1; stringIndex++) {
      pattern[stringIndex] = [];
      
      // Find the next note in scale sequence on this string
      currentScaleIndex = (currentScaleIndex + 1) % activeScale.notes.length;
      const targetNote = activeScale.notes[currentScaleIndex];
      const targetNoteIdx = resolveAllNotesIndex(targetNote);
      
      // Find this note on the current string, preferring positions close to previous string
      const prevStringFirstFret = pattern[stringIndex - 1][0];
      let bestMatch = null;
      let minDistance = Infinity;
      
      for (const noteInfo of scaleMap[stringIndex]) {
        if (resolveAllNotesIndex(noteInfo.note) === targetNoteIdx) {
          const distance = Math.abs(noteInfo.fret - prevStringFirstFret);
          if (distance < minDistance) {
            minDistance = distance;
            bestMatch = noteInfo;
          }
        }
      }
      
      if (bestMatch) {
        pattern[stringIndex].push(bestMatch.fret);
        currentFretPosition = bestMatch.fret;
        
        // Continue adding notes following half-step rule
        let localScaleIndex = currentScaleIndex;
        while (localScaleIndex < activeScale.notes.length - 1) {
          const currentNote = activeScale.notes[localScaleIndex];
          const nextNote = activeScale.notes[(localScaleIndex + 1) % activeScale.notes.length];
          
          const currentNoteIdx = resolveAllNotesIndex(currentNote);
          const nextNoteIdx = resolveAllNotesIndex(nextNote);
          const interval = (nextNoteIdx - currentNoteIdx + 12) % 12;
          
          if (interval === 1) {
            // Half step - add to current string
            const nextNoteFret = currentFretPosition + interval;
            if (nextNoteFret <= 24 && pattern[stringIndex].length < 4) { // Limit notes per string
              pattern[stringIndex].push(nextNoteFret);
              currentFretPosition = nextNoteFret;
              localScaleIndex++;
              currentScaleIndex = localScaleIndex;
            } else {
              break;
            }
          } else {
            break; // Move to next string on larger intervals
          }
        }
      }
    }
    
    // Ensure each string has at least one note
    for (let stringIndex = 0; stringIndex < getGuitarTuning().length; stringIndex++) {
      if (!pattern[stringIndex]) {
        pattern[stringIndex] = [];
      }
      if (pattern[stringIndex].length === 0 && scaleMap[stringIndex].length > 0) {
        // Add the first available note on this string
        pattern[stringIndex].push(scaleMap[stringIndex][0].fret);
      }
    }
    
    // Calculate lowest note for non-diatonic pattern
    let lowestNoteND = Infinity;
    for (let stringIndex = 0; stringIndex < getGuitarTuning().length; stringIndex++) {
      if (pattern[stringIndex] && pattern[stringIndex].length > 0) {
        const minFret = Math.min(...pattern[stringIndex]);
        if (minFret < lowestNoteND) {
          lowestNoteND = minFret;
        }
      }
    }
    lowestNoteND = lowestNoteND === Infinity ? 0 : lowestNoteND;
    
    return { pattern, rootFret: rootFretOnLowE, lowestNoteOnAnyString: lowestNoteND };
  }
  
  // Determine notes per string based on scale size once for entire pattern generation
  const notesPerString = getNotesPerString(activeScale.modeName);
  
  if (rootIndexOnLowE !== -1) {
    // Get consecutive scale notes starting from the root
    pattern[0] = [];
    let notesNeeded = notesPerString;
    let currentIndex = rootIndexOnLowE;
    
    while (notesNeeded > 0 && currentIndex < scaleMap[0].length) {
      pattern[0].push(scaleMap[0][currentIndex].fret);
      notesNeeded--;
      currentIndex++;
    }
  } else {
    // Fallback if root not found
    pattern[0] = scaleMap[0].slice(0, notesPerString).map(n => n.fret);
  }
  
  // Step 4: For each remaining string, find the next notes in the scale sequence
  // Keep track of the current scale index we're at
  let currentScaleIndex = 0;
  if (pattern[0] && pattern[0].length > 0) {
    const lastFret = pattern[0][pattern[0].length - 1];
    // Find the note in scaleMap to get the scale's version of the note
    const lastNoteInfo = scaleMap[0].find(n => n.fret === lastFret);
    if (lastNoteInfo) {
      currentScaleIndex = activeScale.notes.indexOf(lastNoteInfo.note);
    } else {
      // Fallback if not found
      const lastNote = getNoteAtFret(getGuitarTuning()[0], lastFret);
      currentScaleIndex = activeScale.notes.indexOf(lastNote);
    }
  }
  
  // Process each string (A, D, G, B, high E)
  for (let stringIndex = 1; stringIndex < getGuitarTuning().length; stringIndex++) {
    const stringNotes = scaleMap[stringIndex];
    pattern[stringIndex] = [];
    
    // Find the next scale note (wrapping around the scale if needed)
    const nextScaleIndex = (currentScaleIndex + 1) % activeScale.notes.length;
    const nextScaleNote = activeScale.notes[nextScaleIndex];
    const nextScaleNoteIdx = resolveAllNotesIndex(nextScaleNote);
    
    // Find the fret position where this note appears on the current string
    // We want to stay in a reasonable position range relative to the previous string
    const prevStringFirstFret = pattern[stringIndex - 1][0];
    let closestNoteIndex = -1;
    let minDistance = Infinity;
    
    for (let i = 0; i < stringNotes.length; i++) {
      // Compare by note index to handle enharmonics
      const noteIdx = resolveAllNotesIndex(stringNotes[i].note);
      if (noteIdx === nextScaleNoteIdx) {
        const distance = Math.abs(stringNotes[i].fret - prevStringFirstFret);
        if (distance < minDistance) {
          minDistance = distance;
          closestNoteIndex = i;
        }
      }
    }
    
    // If we found the next scale note, use it
    if (closestNoteIndex !== -1) {
      pattern[stringIndex].push(stringNotes[closestNoteIndex].fret);
      
      // Now add the next scale notes (continuing the scale sequence)
      let scaleIdx = nextScaleIndex;
      let addedNotes = 1; // We've already added one note
      
      while (addedNotes < notesPerString) {
        // Move to the next scale note
        scaleIdx = (scaleIdx + 1) % activeScale.notes.length;
        const nextNote = activeScale.notes[scaleIdx];
        const nextNoteIdx = resolveAllNotesIndex(nextNote);
        
        // Find this note on the current string (must be higher on the fretboard than our last note)
        const lastAddedFret = pattern[stringIndex][pattern[stringIndex].length - 1];
        
        let found = false;
        for (const note of stringNotes) {
          // Compare by note index to handle enharmonics
          const noteIdx = resolveAllNotesIndex(note.note);
          if (noteIdx === nextNoteIdx && note.fret > lastAddedFret) {
            pattern[stringIndex].push(note.fret);
            addedNotes++;
            found = true;
            break;
          }
        }
        
        // If we couldn't find the next note, we've reached the end of the string
        if (!found) {
          break;
        }
      }
      
      // If we don't have enough notes, add more from what's available
      if (pattern[stringIndex].length < notesPerString) {
        const lastAddedFret = pattern[stringIndex][pattern[stringIndex].length - 1];
        const remainingNotes = notesPerString - pattern[stringIndex].length;
        
        // Find additional notes above our last added fret
        const additionalNotes = stringNotes
          .filter(n => n.fret > lastAddedFret)
          .slice(0, remainingNotes)
          .map(n => n.fret);
        
        pattern[stringIndex].push(...additionalNotes);
      }
    } else {
      // If we couldn't find the next scale note, find notes in a similar position
      // to the first note of the previous string
      const nearbyNotes = stringNotes
        .filter(n => Math.abs(n.fret - prevStringFirstFret) < 5)
        .slice(0, notesPerString)
        .map(n => n.fret);
      
      if (nearbyNotes.length > 0) {
        pattern[stringIndex] = nearbyNotes;
      } else {
        // Last resort: use the first available notes on this string
        pattern[stringIndex] = stringNotes.slice(0, notesPerString).map(n => n.fret);
      }
    }
    
    // Update the current scale index for the next string
    if (pattern[stringIndex].length > 0) {
      const lastFret = pattern[stringIndex][pattern[stringIndex].length - 1];
      // Find the note in scaleMap to get the scale's version of the note
      const lastNoteInfo = stringNotes.find(n => n.fret === lastFret);
      if (lastNoteInfo) {
        currentScaleIndex = activeScale.notes.indexOf(lastNoteInfo.note);
      } else {
        // Fallback if not found
        const lastNote = getNoteAtFret(getGuitarTuning()[stringIndex], lastFret);
        currentScaleIndex = activeScale.notes.indexOf(lastNote);
      }
    }
  }
  
  // Calculate the lowest note on any string from the current pattern
  let lowestNoteOnAnyString = Infinity;
  Object.values(pattern).forEach(stringFrets => {
    if (stringFrets && stringFrets.length > 0) {
      const minFretOnString = Math.min(...stringFrets);
      lowestNoteOnAnyString = Math.min(lowestNoteOnAnyString, minFretOnString);
    }
  });
  lowestNoteOnAnyString = lowestNoteOnAnyString === Infinity ? 0 : lowestNoteOnAnyString;
  
  return { pattern, rootFret: rootFretOnLowE, lowestNoteOnAnyString };
};

const generateAnchoredPattern = (activeScale, { startStringIndex, startFret, totalStrings }) => {
  if (!activeScale || !activeScale.notes || activeScale.notes.length === 0) {
    return { pattern: [], rootFret: startFret, lowestNoteOnAnyString: 0 };
  }

  const pattern = Array.from({ length: totalStrings }, () => []);
  const stringsToProcess = [];
  for (let s = startStringIndex; s < totalStrings; s++) {
    stringsToProcess.push(s);
  }

  const scaleNotes = activeScale.notes;
  const scaleNoteCount = scaleNotes.length;
  const modeData = MODES[activeScale.modeName];
  const usePentatonicPattern = modeData && (modeData.isPentatonic || scaleNoteCount <= 6);
  const useNonDiatonicPattern = modeData && !modeData.isPentatonic && scaleNoteCount >= 8;
  const notesPerString = getNotesPerString(activeScale.modeName);

  // Build scale map once so all branches can reuse it
  const scaleMap = {};
  const scaleIdx = scaleIndexSet(scaleNotes);
  getGuitarTuning().forEach((openNote, stringIndex) => {
    const entries = [];
    for (let fret = 0; fret <= 24; fret++) {
      const fretIdx = (ALL_NOTES.indexOf(openNote) + fret) % 12;
      if (scaleIdx.has(fretIdx)) {
        const note = getNoteAtFret(openNote, fret);
        const scaleNote = scaleNotes.find(n => resolveAllNotesIndex(n) === fretIdx);
        entries.push({
          fret,
          note: scaleNote || note,
          degree: scaleNotes.indexOf(scaleNote || note)
        });
      }
    }
    scaleMap[stringIndex] = entries.sort((a, b) => a.fret - b.fret);
  });

  const startStringNotes = scaleMap[startStringIndex] || [];
  if (startStringNotes.length === 0) {
    return { pattern, rootFret: startFret, lowestNoteOnAnyString: 0 };
  }

  const anchorNoteValue = getNoteAtFret(getGuitarTuning()[startStringIndex], startFret);
  const anchorNoteIdx = resolveAllNotesIndex(anchorNoteValue);
  let anchorScaleIndex = scaleNotes.findIndex(n => resolveAllNotesIndex(n) === anchorNoteIdx);
  if (anchorScaleIndex === -1) {
    anchorScaleIndex = startStringNotes[0]?.degree ?? 0;
  }

  // Locate the starting entry on the anchor string
  let anchorEntry = startStringNotes.find(n => n.fret === startFret);
  if (!anchorEntry) {
    anchorEntry = startStringNotes.find(n => resolveAllNotesIndex(n.note) === anchorNoteIdx && n.fret >= startFret)
      || startStringNotes.find(n => resolveAllNotesIndex(n.note) === anchorNoteIdx)
      || startStringNotes[0];
  }

  const effectiveStartFret = anchorEntry ? anchorEntry.fret : startFret;
  if (usePentatonicPattern) {
    const MAX_SPAN = 5;
    let currentScaleDegree = anchorEntry?.degree ?? anchorScaleIndex ?? 0;
    if (currentScaleDegree < 0) currentScaleDegree = 0;
    let previousProcessedString = null;

    stringsToProcess.forEach((stringIndex, orderIndex) => {
      const stringNotes = scaleMap[stringIndex];
      if (!stringNotes || stringNotes.length === 0) {
        pattern[stringIndex] = [];
        return;
      }

      const frets = [];
      let remainingStringSpace = MAX_SPAN;
      const isFirstString = orderIndex === 0;
      const referenceFret = previousProcessedString !== null && pattern[previousProcessedString].length > 0
        ? pattern[previousProcessedString][0]
        : effectiveStartFret;

      let f1 = null;
      if (isFirstString) {
        f1 = effectiveStartFret;
        if (!stringNotes.some(n => n.fret === f1)) {
          const fallback = stringNotes.find(n => resolveAllNotesIndex(n.note) === anchorNoteIdx) || stringNotes[0];
          f1 = fallback?.fret ?? effectiveStartFret;
          currentScaleDegree = fallback?.degree ?? currentScaleDegree;
        }
      } else {
        const nextScaleDegree = (currentScaleDegree + 1) % scaleNotes.length;
        const targetNoteIdx = resolveAllNotesIndex(scaleNotes[nextScaleDegree]);
        let minDistance = Infinity;
        for (const noteInfo of stringNotes) {
          if (resolveAllNotesIndex(noteInfo.note) === targetNoteIdx) {
            const distance = Math.abs(noteInfo.fret - referenceFret);
            if (distance < minDistance) {
              minDistance = distance;
              f1 = noteInfo.fret;
              currentScaleDegree = nextScaleDegree;
            }
          }
        }
      }

      if (f1 === null || f1 === undefined) {
        pattern[stringIndex] = [];
        return;
      }

      frets.push(f1);

      const secondDegree = (currentScaleDegree + 1) % scaleNotes.length;
      const secondNoteIdx = resolveAllNotesIndex(scaleNotes[secondDegree]);
      let f2 = null;
      for (const noteInfo of stringNotes) {
        if (resolveAllNotesIndex(noteInfo.note) === secondNoteIdx && noteInfo.fret > f1 && (noteInfo.fret - f1) <= MAX_SPAN) {
          f2 = noteInfo.fret;
          currentScaleDegree = secondDegree;
          break;
        }
      }

      if (f2 !== null) {
        const distanceF1toF2 = f2 - f1;
        remainingStringSpace -= distanceF1toF2;
        if (remainingStringSpace >= 1) {
          frets.push(f2);
          const thirdDegree = (currentScaleDegree + 1) % scaleNotes.length;
          const thirdNoteIdx = resolveAllNotesIndex(scaleNotes[thirdDegree]);
          let f3 = null;
          for (const noteInfo of stringNotes) {
            if (resolveAllNotesIndex(noteInfo.note) === thirdNoteIdx && noteInfo.fret > f2 && (noteInfo.fret - f1) <= MAX_SPAN) {
              f3 = noteInfo.fret;
              break;
            }
          }

          if (f3 !== null) {
            const distanceF2toF3 = f3 - f2;
            const usedLargeInterval = distanceF1toF2 > 1;
            if (!usedLargeInterval || distanceF2toF3 <= 1) {
              remainingStringSpace -= distanceF2toF3;
              if (remainingStringSpace >= 1) {
                frets.push(f3);
                currentScaleDegree = thirdDegree;
              }
            }
          }
        }
      }

      pattern[stringIndex] = frets;
      previousProcessedString = stringIndex;
    });
  } else if (useNonDiatonicPattern) {
    let currentScaleIndex = anchorEntry?.degree ?? anchorScaleIndex ?? 0;
    let currentFretPosition = effectiveStartFret;

    pattern[startStringIndex].push(effectiveStartFret);

    while (true) {
      const nextIndex = (currentScaleIndex + 1) % scaleNotes.length;
      const currentNoteIdx = resolveAllNotesIndex(scaleNotes[currentScaleIndex]);
      const nextNoteIdx = resolveAllNotesIndex(scaleNotes[nextIndex]);
      const interval = (nextNoteIdx - currentNoteIdx + 12) % 12;
      if (interval !== 1) break;
      const nextFret = currentFretPosition + interval;
      if (nextFret > 24) break;
      pattern[startStringIndex].push(nextFret);
      currentFretPosition = nextFret;
      currentScaleIndex = nextIndex;
    }

    let prevStringIndex = startStringIndex;
    stringsToProcess.slice(1).forEach(stringIndex => {
      const stringNotes = scaleMap[stringIndex];
      if (!stringNotes || stringNotes.length === 0) {
        pattern[stringIndex] = [];
        return;
      }

      currentScaleIndex = (currentScaleIndex + 1) % scaleNotes.length;
      const targetIdx = resolveAllNotesIndex(scaleNotes[currentScaleIndex]);
      const prevFret = pattern[prevStringIndex][0];
      let bestMatch = null;
      let minDistance = Infinity;
      for (const noteInfo of stringNotes) {
        if (resolveAllNotesIndex(noteInfo.note) === targetIdx) {
          const distance = Math.abs(noteInfo.fret - prevFret);
          if (distance < minDistance) {
            minDistance = distance;
            bestMatch = noteInfo;
          }
        }
      }

      if (bestMatch) {
        pattern[stringIndex].push(bestMatch.fret);
        currentFretPosition = bestMatch.fret;
        let localIndex = currentScaleIndex;
        while (true) {
          const nextIndex = (localIndex + 1) % scaleNotes.length;
          const currentIdx = resolveAllNotesIndex(scaleNotes[localIndex]);
          const nextIdx = resolveAllNotesIndex(scaleNotes[nextIndex]);
          const interval = (nextIdx - currentIdx + 12) % 12;
          if (interval !== 1) break;
          const nextFret = currentFretPosition + interval;
          if (nextFret > 24 || pattern[stringIndex].length >= 4) break;
          pattern[stringIndex].push(nextFret);
          currentFretPosition = nextFret;
          localIndex = nextIndex;
          currentScaleIndex = nextIndex;
        }
      }

      prevStringIndex = stringIndex;
    });
  } else {
    let currentScaleIndex = anchorEntry?.degree ?? anchorScaleIndex ?? 0;
    const anchorIndexOnString = startStringNotes.findIndex(n => n.fret === effectiveStartFret);
    let notesNeeded = notesPerString;
    let localIndex = anchorIndexOnString;
    while (notesNeeded > 0 && localIndex < startStringNotes.length) {
      pattern[startStringIndex].push(startStringNotes[localIndex].fret);
      currentScaleIndex = startStringNotes[localIndex].degree ?? currentScaleIndex;
      notesNeeded--;
      localIndex++;
    }
    if (pattern[startStringIndex].length === 0) {
      pattern[startStringIndex].push(startStringNotes[0].fret);
      currentScaleIndex = startStringNotes[0].degree ?? currentScaleIndex;
    }

    let previousStringIndex = startStringIndex;
    stringsToProcess.slice(1).forEach(stringIndex => {
      const stringNotes = scaleMap[stringIndex];
      if (!stringNotes || stringNotes.length === 0) {
        pattern[stringIndex] = [];
        return;
      }

      const nextScaleIndex = (currentScaleIndex + 1) % scaleNotes.length;
      const targetIdx = resolveAllNotesIndex(scaleNotes[nextScaleIndex]);
      const prevFret = pattern[previousStringIndex][0];
      let closestMatchIndex = -1;
      let minDistance = Infinity;
      for (let i = 0; i < stringNotes.length; i++) {
        if (resolveAllNotesIndex(stringNotes[i].note) === targetIdx) {
          const distance = Math.abs(stringNotes[i].fret - prevFret);
          if (distance < minDistance) {
            minDistance = distance;
            closestMatchIndex = i;
          }
        }
      }

      if (closestMatchIndex !== -1) {
        pattern[stringIndex].push(stringNotes[closestMatchIndex].fret);
        let addedNotes = 1;
        let scaleIdx = stringNotes[closestMatchIndex].degree ?? nextScaleIndex;
        while (addedNotes < notesPerString) {
          scaleIdx = (scaleIdx + 1) % scaleNotes.length;
          const noteIdx = resolveAllNotesIndex(scaleNotes[scaleIdx]);
          const lastFret = pattern[stringIndex][pattern[stringIndex].length - 1];
          const nextNote = stringNotes.find(n => resolveAllNotesIndex(n.note) === noteIdx && n.fret > lastFret);
          if (!nextNote) break;
          pattern[stringIndex].push(nextNote.fret);
          addedNotes++;
        }
        currentScaleIndex = pattern[stringIndex].length
          ? stringNotes.find(n => n.fret === pattern[stringIndex][pattern[stringIndex].length - 1])?.degree ?? currentScaleIndex
          : currentScaleIndex;
      } else {
        pattern[stringIndex] = stringNotes.slice(0, notesPerString).map(n => n.fret);
        currentScaleIndex = stringNotes[pattern[stringIndex].length - 1]?.degree ?? currentScaleIndex;
      }

      previousStringIndex = stringIndex;
    });
  }

  let lowestNoteOnAnyString = Infinity;
  for (const stringFrets of pattern) {
    if (stringFrets.length > 0) {
      lowestNoteOnAnyString = Math.min(lowestNoteOnAnyString, Math.min(...stringFrets));
    }
  }
  lowestNoteOnAnyString = lowestNoteOnAnyString === Infinity ? 0 : lowestNoteOnAnyString;

  return { pattern, rootFret: effectiveStartFret, lowestNoteOnAnyString };
};

// Hook for managing pattern generation
export const usePatternGeneration = (activeScale) => {
  const guitarTuning = useGuitarTuning();
  const [pattern, setPattern] = useState([]);
  const [rootFretOnLowE, setRootFretOnLowE] = useState(0);
  const [lowestNoteOnAnyString, setLowestNoteOnAnyString] = useState(0);
  
  useEffect(() => {

    if (activeScale) {
      const { pattern: newPattern, rootFret, lowestNoteOnAnyString: lowestNote } = generateNotePattern(activeScale);

      // Also generate pattern at the octave root position and merge
      const octaveFret = rootFret + 12;
      if (octaveFret <= 24) {
        const { pattern: octavePattern = [] } = generateNotePattern(activeScale, {
          anchorToStartNote: true,
          startStringIndex: 0,
          startFret: octaveFret
        }) || {};

        octavePattern.forEach((frets, sIdx) => {
          if (!newPattern[sIdx]) newPattern[sIdx] = [];
          if (Array.isArray(frets)) {
            frets.forEach(f => {
              if (!newPattern[sIdx].includes(f)) {
                newPattern[sIdx].push(f);
              }
            });
          }
        });
      }

      setPattern(newPattern);
      setRootFretOnLowE(rootFret);
      setLowestNoteOnAnyString(lowestNote);
    }
    // The 3-notes-per-string pattern depends only on the scale SHAPE
    // (root + mode + notes) and the tuning, not on chord highlighting. Keying on
    // the object identity of activeScale regenerated the whole pattern on every
    // chord tap, even when only voicingTonePcs/chordTones changed. Key on the
    // shape instead. The tuning is a dep because Play stays mounted while the
    // Settings save a new one: the frets must be recomputed for the new strings.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScale?.scaleRoot, activeScale?.modeName, activeScale?.notes?.join(','), guitarTuning]);
  
  // Check if a note is in the pattern (3 notes per string)
  const isNoteInPattern = React.useCallback((stringIndex, fret) => {
    if (!pattern[stringIndex]) return false;
    return pattern[stringIndex].includes(fret);
  }, [pattern]);

  return { pattern, isNoteInPattern, rootFretOnLowE, lowestNoteOnAnyString };
};

// Hook for managing preset functionality
export const usePresetManagement = (rootNote, currentMode, selectedNotes, visibleStrings, hasSelectedNote, activeScale) => {
  
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presets, setPresets] = useState({});
  const [loadPresetDialogOpen, setLoadPresetDialogOpen] = useState(false);
  
  // Load presets on component mount
  useEffect(() => {
    const savedPresets = getPatternPresets();
    setPresets(savedPresets);
  }, []);
  
  // Save pattern preset
  const savePreset = (updater) => {
    if (!presetName.trim()) {
      return; // Don't save if name is empty
    }
    
    const preset = {
      rootNote,
      currentMode,
      selectedNotes,
      visibleStrings: visibleStrings ? Array.from(visibleStrings) : null,
      hasSelectedNote,
      activeScale
    };
    
    const success = savePatternPreset(presetName, preset);
    if (success) {
      setPresets(getPatternPresets());
      setPresetDialogOpen(false);
      setPresetName('');
    }
  };
  
  // Load pattern preset
  const loadPreset = (presetName, updateFunctions) => {
    const preset = presets[presetName];
    
    if (preset) {
      // Update component state with preset values
      updateFunctions.setSelectedNotes(preset.selectedNotes || []);
      updateFunctions.setHasSelectedNote(preset.hasSelectedNote || false);
      
      // Handle visibleStrings (convert array back to Set if needed)
      if (preset.visibleStrings) {
        updateFunctions.setVisibleStrings(new Set(preset.visibleStrings));
      } else {
        updateFunctions.setVisibleStrings(null);
      }
      
      setLoadPresetDialogOpen(false);
    }
  };
  
  // Delete pattern preset
  const deletePreset = (presetName) => {
    deletePatternPreset(presetName);
    setPresets(getPatternPresets());
  };
  
  return {
    presetDialogOpen,
    setPresetDialogOpen,
    presetName,
    setPresetName,
    presets,
    loadPresetDialogOpen,
    setLoadPresetDialogOpen,
    savePreset,
    loadPreset,
    deletePreset
  };
};

// Pure helpers (all inputs are call arguments): defined at module scope so their
// identity is stable and memoized StringRow/TouchableNoteMarker don't re-render.

// Determine if a note is in the arpeggio
const isArpeggioNoteImpl = (stringIndex, fret, activeScale, selectedArpeggio) => {
    if (!activeScale) return false;
    
    // If no arpeggio is selected, don't highlight any notes
    if (!selectedArpeggio) {
      return false;
    }
    
    // When an arpeggio is selected, check if this note is in that arpeggio
    const openNote = getGuitarTuning()[stringIndex];
    const note = getNoteAtFret(openNote, fret);
    
    // Normalize note name to handle potential enharmonic differences
    const normalizedNote = note.replace(/[♯#]/g, '#').replace(/[♭b]/g, 'b');
    
    // Check each note in the arpeggio, normalizing them the same way
    return selectedArpeggio.notes.some(arpeggioNote => {
      const normalizedArpeggioNote = arpeggioNote.replace(/[♯#]/g, '#').replace(/[♭b]/g, 'b');
      return normalizedArpeggioNote === normalizedNote;
    });
};

// Get the note degree for display
const getArpeggioDegreeImpl = (stringIndex, fret, activeScale, selectedArpeggio) => {
    if (!activeScale) return '';
    
    // If no arpeggio is selected, don't show any degree numbers
    if (!selectedArpeggio) {
      return '';
    }
    
    // When an arpeggio is selected, get the degree position in that arpeggio
    const openNote = getGuitarTuning()[stringIndex];
    const note = getNoteAtFret(openNote, fret);
    
    // Normalize note name to handle potential enharmonic differences
    const normalizedNote = note.replace(/[♯#]/g, '#').replace(/[♭b]/g, 'b');
    
    // Find the position in the arpeggio
    let position = -1;
    selectedArpeggio.notes.forEach((arpeggioNote, index) => {
      const normalizedArpeggioNote = arpeggioNote.replace(/[♯#]/g, '#').replace(/[♭b]/g, 'b');
      if (normalizedArpeggioNote === normalizedNote) {
        position = index;
      }
    });
    
    // Map position to actual scale degree based on intervals
    if (position >= 0 && selectedArpeggio.intervals) {
      // Use the intervals from the arpeggio to determine the scale degree
      // Intervals are typically 0-based relative to the root, like [0, 4, 7] for major chord
      // We convert to 1-based scale degrees (1, 3, 5)
      // The intervals array in the selectedArpeggio object contains the semitone intervals
      // For example, major triad has intervals [0, 4, 7] which represent the root (0), major third (4), and perfect fifth (7)
      const interval = selectedArpeggio.intervals[position];
      
      // Map common intervals to scale degrees
      const intervalToDegree = {
        0: '1',  // Root
        2: '2',  // Second (for sus2)
        3: 'b3', // Minor third
        4: '3',  // Major third
        5: '4',  // Perfect fourth
        7: '5',  // Perfect fifth
        9: '6',  // Sixth
        10: 'b7', // Minor seventh
        11: '7',  // Major seventh
      };
      
      // For debugging
      
      return intervalToDegree[interval] || (position + 1).toString();
    }
    
    // Fallback to position + 1 if no intervals are available
    return position >= 0 ? (position + 1).toString() : '';
};

// Hook for managing arpeggio highlighting
export const useArpeggioHighlighting = () => {
  const [highlightArpeggios, setHighlightArpeggios] = useState(false);
  const [selectedArpeggio, setSelectedArpeggio] = useState(null);

  return {
    highlightArpeggios,
    setHighlightArpeggios,
    selectedArpeggio,
    setSelectedArpeggio,
    isArpeggioNote: isArpeggioNoteImpl,
    getArpeggioDegree: getArpeggioDegreeImpl
  };
};

// Stable empty array so consumers keep the same identity while highlighting is off
const EMPTY_CHORD_NOTES = [];

// Hook for managing chord highlighting with target interval types
export const useChordHighlighting = (activeChordNote, playingNote, activeScale, selectedIntervalTypes = ['root']) => {
  const [highlightChordNotes, setHighlightChordNotes] = useState(false);

  // Root used by isChordNote/getChordDegree. Null while highlighting is off so
  // per-tap playingNote changes don't churn the memoized callbacks below.
  const chordHighlightRoot = highlightChordNotes
    ? (activeChordNote || playingNote || activeScale?.scaleRoot || null)
    : null;
  
  // Helper function to determine chord quality based on scale context
  // DEAD CODE, never called. Reads mode.chordQualities, which is wrong for the
  // 10 pentatonic modes (live pads use getPentatonicChordTypes). Fix
  // chord_qualities in the database before reviving this path.
  // See docs/plans/pentatonic-chord-naming.md.
  const getChordQuality = (chordRoot, activeScale) => {
    if (!activeScale || !activeScale.modeName || !chordRoot) return 'maj';
    
    // Get notes in the current scale
    const scaleNotes = getNotesInMode(activeScale.scaleRoot, activeScale.modeName);
    
    // Find the chord root's position in the scale (by pitch class to handle
    // enharmonics: the scale may be spelled with flats vs a sharp chordRoot)
    const chordRootPitch = resolveAllNotesIndex(chordRoot);
    const chordRootIndex = scaleNotes.findIndex(n => resolveAllNotesIndex(n) === chordRootPitch);
    
    // If chord root is not in the scale, default to major
    if (chordRootIndex === -1) return 'maj';
    
    // Get chord qualities for this mode from the mode definition
    const modeData = MODES[activeScale.modeName];
    if (!modeData || !modeData.chordQualities) return 'maj';
    
    const chordQualities = modeData.chordQualities;
    
    // If no chord qualities defined for this mode, default to major
    if (!chordQualities || !chordQualities[chordRootIndex]) return 'maj';
    
    return chordQualities[chordRootIndex];
  };

  // Get chord notes for the selected interval types - memoized to update when dependencies change
  const chordNotesToHighlight = React.useMemo(() => {
    if (!highlightChordNotes) return EMPTY_CHORD_NOTES;

    const chordRoot = activeChordNote || playingNote || activeScale?.scaleRoot;
    if (!chordRoot) return EMPTY_CHORD_NOTES;

    const rootIndex = resolveAllNotesIndex(chordRoot);
    if (rootIndex === -1) return EMPTY_CHORD_NOTES;
    
    // Prefer the already-computed active scale notes when available
    const scaleNotesRaw = activeScale?.notes?.length ? activeScale.notes : getNotesInMode(activeScale.scaleRoot, activeScale.modeName);
    const scaleNotes = Array.isArray(scaleNotesRaw) ? scaleNotesRaw : [];
    const scaleLength = scaleNotes.length;
    
    // Locate the chord root inside the scale (by pitch-class to handle enharmonics)
    const rootScaleIndex = scaleNotes.findIndex(note => resolveAllNotesIndex(note) === rootIndex);
    const isHeptatonic = scaleLength === 7 && rootScaleIndex !== -1;
    
    const scaleIdx = scaleIndexSet(scaleNotes);
    const collectedNotes = [];
    
    const addNoteByName = (noteName) => {
      if (!noteName) return false;
      const idx = resolveAllNotesIndex(noteName);
      if (idx === -1) return false;
      collectedNotes.push(ALL_NOTES[idx]);
      return true;
    };
    
    const addStackedDegree = (degreeStep) => {
      if (!isHeptatonic) return false;
      const targetIndex = (rootScaleIndex + degreeStep * 2) % scaleLength;
      const stackedNote = scaleNotes[targetIndex];
      return addNoteByName(stackedNote);
    };
    
    const addIntervalIfAvailable = (intervalOptions = []) => {
      for (const interval of intervalOptions) {
        if (scaleIdx.has((rootIndex + interval) % 12)) {
          return addNoteByName(ALL_NOTES[(rootIndex + interval) % 12]);
        }
      }
      return false;
    };
    
    selectedIntervalTypes.forEach(type => {
      switch(type) {
        case 'root':
          addNoteByName(chordRoot);
          break;
        case 'third':
          if (!addStackedDegree(1)) {
            addIntervalIfAvailable([4, 3]);
          }
          break;
        case 'fifth':
          if (!addStackedDegree(2)) {
            addIntervalIfAvailable([7, 6, 8]);
          }
          break;
        case 'seventh':
          if (!addStackedDegree(3)) {
            addIntervalIfAvailable([11, 10, 9]); // Allow diminished sevenths (bb7) fallback
          }
          break;
        case 'ninth':
          if (!addStackedDegree(4)) {
            addIntervalIfAvailable([14, 13, 15]);
          }
          break;
        case 'eleventh':
          if (!addStackedDegree(5)) {
            addIntervalIfAvailable([17, 18]);
          }
          break;
        case 'thirteenth':
          if (!addStackedDegree(6)) {
            addIntervalIfAvailable([21, 20]);
          }
          break;
      }
    });
    
    const uniqueNotes = [...new Set(collectedNotes)];
    
    
    
    return uniqueNotes;
  }, [highlightChordNotes, activeChordNote, playingNote, activeScale, selectedIntervalTypes]);
  
  // Determine if a note is in the current chord
  const isChordNote = React.useCallback((stringIndex, fret, activeScale) => {
    if (!activeScale || !highlightChordNotes) return false;

    const openNote = getGuitarTuning()[stringIndex];
    const note = getNoteAtFret(openNote, fret);
    const noteIdx = resolveAllNotesIndex(note);

    // Compare by pitch-class index to handle enharmonic equivalents (A# vs Bb)
    return chordNotesToHighlight.some(chordNote => resolveAllNotesIndex(chordNote) === noteIdx);
  }, [highlightChordNotes, chordNotesToHighlight]);

  // Get the chord degree for display (1, 3, 5, etc.)
  const getChordDegree = React.useCallback((stringIndex, fret, activeScale) => {
    if (!activeScale || !highlightChordNotes) return '';

    const openNote = getGuitarTuning()[stringIndex];
    const note = getNoteAtFret(openNote, fret);

    // Get the chord root
    const chordRoot = chordHighlightRoot;
    if (!chordRoot) return '';

    // Find indices using resolveAllNotesIndex to handle enharmonics
    const rootIndex = resolveAllNotesIndex(chordRoot);
    const noteIndex = resolveAllNotesIndex(note);
    
    if (rootIndex === -1 || noteIndex === -1) return '';
    
    // Calculate semitone distance from root
    let semitones = (noteIndex - rootIndex + 12) % 12;
    
    // Map semitones to interval degrees
    // Handle octave equivalents (e.g., 14 = 2 = 9th)
    switch(semitones) {
      case 0: return '1';  // Root
      case 1: return '♭9'; // Minor 9th (rare)
      case 2: return '9';  // Major 9th
      case 3: return '3';  // Minor 3rd
      case 4: return '3';  // Major 3rd
      case 5: return '11'; // Perfect 11th
      case 6: return '♭5'; // Diminished 5th (rare)
      case 7: return '5';  // Perfect 5th
      case 8: return '♯5'; // Augmented 5th (rare)
      case 9: return '13'; // Major 13th (or 6th)
      case 10: return '7'; // Minor 7th
      case 11: return '7'; // Major 7th
      default: return '';
    }
  }, [highlightChordNotes, chordHighlightRoot]);

  return {
    highlightChordNotes,
    setHighlightChordNotes,
    chordNotesToHighlight,
    isChordNote,
    getChordDegree
  };
};

// Hook for managing connection lines visibility
export const useConnectionLines = () => {
  const [showConnectionLines, setShowConnectionLines] = useState(false);
  
  return {
    showConnectionLines,
    setShowConnectionLines
  };
};

// Hook for checking if notes are in scale and if they are root notes
export const useScaleNotes = (activeScale) => {
  
  // Check if a note is in the scale
  const isNoteInScale = (stringIndex, fret) => {
    if (!activeScale) return false;
    
    const openNote = getGuitarTuning()[stringIndex];
    const fretIdx = (ALL_NOTES.indexOf(openNote) + fret) % 12;
    const scaleIdx = scaleIndexSet(activeScale.notes);
    
    return scaleIdx.has(fretIdx);
  };
  
  // Check if a note is the root note (the actual scale root, not necessarily the overall key root)
  const isRootNote = (stringIndex, fret) => {
    if (!activeScale) return false;
    
    const openNote = getGuitarTuning()[stringIndex];
    const fretIdx = (ALL_NOTES.indexOf(openNote) + fret) % 12;
    const rootIdx = resolveAllNotesIndex(activeScale.scaleRoot);
    
    return fretIdx === rootIdx;
  };
  
  return {
    isNoteInScale,
    isRootNote
  };
};
